<script setup lang="ts">
/**
 * UiKitTextarea — многострочное поле HeroUI UI-kit (по аналогии с UiKitInput).
 * Цвета только из токенов (классы hui-*).
 */
import { FOCUS_BORDER, type HuiColor } from './palette'

type TextareaVariant = 'flat' | 'bordered' | 'faded'
type InputRadius = 'none' | 'sm' | 'md' | 'lg'

const props = withDefaults(
  defineProps<{
    color?: HuiColor
    variant?: TextareaVariant
    radius?: InputRadius
    label?: string
    placeholder?: string
    description?: string
    errorMessage?: string
    rows?: number
    disabled?: boolean
    invalid?: boolean
    readonly?: boolean
    resize?: boolean
  }>(),
  {
    color: 'primary',
    variant: 'flat',
    radius: 'md',
    label: '',
    placeholder: '',
    description: '',
    errorMessage: '',
    rows: 3,
    disabled: false,
    invalid: false,
    readonly: false,
    resize: true,
  },
)

const model = defineModel<string>({ default: '' })

defineOptions({ name: 'UiKitTextarea' })

const RADIUS: Record<InputRadius, string> = {
  none: 'rounded-none', sm: 'rounded-hui-sm', md: 'rounded-hui-md', lg: 'rounded-hui-lg',
}

const wrapperClass = computed(() => {
  const c = props.color
  const inv = props.invalid
  const base = 'w-full box-border px-3.5 py-2.5 transition-colors duration-150'
  const idleBordered = inv ? 'border-hui-danger' : `border-hui-default-200 hover:border-hui-default-400 ${FOCUS_BORDER[c]}`
  let variant: string
  switch (props.variant) {
    case 'flat': {
      variant = inv
        ? 'bg-hui-danger/10 border-2 border-transparent'
        : 'bg-hui-default-100 border-2 border-transparent hover:bg-hui-default-200 focus-within:bg-hui-default-200'
      break
    }
    case 'bordered': {
      variant = `bg-transparent border-2 ${idleBordered}`
      break
    }
    case 'faded': {
      variant = `bg-hui-default-100 border-2 ${idleBordered}`
      break
    }
    default: {
      variant = ''
    }
  }
  return [base, RADIUS[props.radius], variant, props.disabled ? 'opacity-50 pointer-events-none' : '']
})

const labelClass = computed(() => ['text-sm', props.invalid ? 'text-hui-danger' : 'text-hui-foreground/70'])
</script>

<template>
  <div class="flex flex-col gap-1.5 w-full">
    <label v-if="label" class="font-medium leading-none" :class="labelClass">{{ label }}</label>

    <div :class="wrapperClass">
      <textarea
        v-model="model"
        :rows="rows"
        :placeholder="placeholder"
        :disabled="disabled"
        :readonly="readonly"
        class="w-full bg-transparent outline-none text-hui-foreground placeholder:text-hui-foreground/40 disabled:cursor-not-allowed"
        :class="resize ? 'resize-y' : 'resize-none'"
      />
    </div>

    <p v-if="invalid && errorMessage" class="text-xs text-hui-danger">{{ errorMessage }}</p>
    <p v-else-if="description" class="text-xs text-hui-foreground/60">{{ description }}</p>
  </div>
</template>
